import { CalendlyExpert } from '@/features/experts/types/CalendlyExpert';
import { TimeSlot } from '@/features/experts/types/TimeSlot';
import { getEventTypeByRole } from '@/features/experts/utils/getUrlSuffix';
import { getAvailableSlots } from '@/utils/apis/booking-service';
import { useAuthStore } from '@/utils/stores/useAuthStore';
import { useUserStore } from '@/utils/stores/useUserStore';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseExpertBookingProps {
    expert: CalendlyExpert;
    onBookClick: (expert: CalendlyExpert) => void;
}

export const useExpertBooking = ({ expert, onBookClick }: UseExpertBookingProps) => {
    const { auth } = useAuthStore();
    const { userData } = useUserStore();
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [monthAvailabilities, setMonthAvailabilities] = useState<Record<string, TimeSlot[]>>({});
    const [isLoadingMonth, setIsLoadingMonth] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
    const [showCalendlyWidget, setShowCalendlyWidget] = useState(false);
    const lastFetchedMonthRef = useRef<string | null>(null);

    // Function to get days in month
    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 = Sunday, 1 = Monday, etc.
        const days = [];
        
        // Adjust first day to start from Monday (1) instead of Sunday (0)
        const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
        
        // Add empty cells for days before the first day of the month
        for (let i = 0; i < adjustedFirstDay; i++) {
            days.push(null);
        }
        
        // Add all days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }
        
        return days;
    };

    // Function to check if a date is today
    const isToday = (date: Date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    // Function to check if a date is in the past
    const isPastDate = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return compareDate < today;
    };

    // Function to check if we can navigate to previous month
    const canNavigateToPreviousMonth = () => {
        const today = new Date();
        const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const todayMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return currentMonthStart > todayMonthStart;
    };

    // Function to format date to YYYY-MM-DD without timezone issues
    const formatDateToYYYYMMDD = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Function to fetch month availabilities
    const fetchMonthAvailabilities = useCallback(async (date: Date) => {
        if (!expert.calendly_data?.calendly_user.uri || !auth.idToken) return;

        // Check if we already fetched this month
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        if (lastFetchedMonthRef.current === monthKey) return;

        // Initialize all days as not available
        const initialAvailabilities: Record<string, TimeSlot[]> = {};
        const days = getDaysInMonth(date);
        days.forEach(day => {
            if (day && !isPastDate(day)) {
                initialAvailabilities[formatDateToYYYYMMDD(day)] = [];
            }
        });
        setMonthAvailabilities(initialAvailabilities);

        setIsLoadingMonth(true);
        try {
            // Calculate start and end dates for the month
            const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
            // Set end date to the last day of the month at 23:59:59
            const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

            // Get the event type for this expert based on their role
            const eventType = getEventTypeByRole(expert.email);
            if (!eventType) {
                throw new Error('No event type found for this expert');
            }

            // Get available slots from the booking service
            const response = await getAvailableSlots(
                auth.idToken,
                expert.calendly_data.calendly_user.uri,
                startDate.toISOString(),
                endDate.toISOString(),
                eventType
            );
            
            // Group slots by date
            const availabilities: Record<string, TimeSlot[]> = {};
            
            // Handle the response based on its structure
            if (response && typeof response === 'object') {
                // If response has available_slots array
                if ('available_slots' in response && Array.isArray(response.available_slots)) {
                    response.available_slots.forEach((slot: any) => {
                        if (slot.start_time && slot.end_time) {
                            const date = new Date(slot.start_time);
                            const dateStr = formatDateToYYYYMMDD(date);
                            
                            if (!availabilities[dateStr]) {
                                availabilities[dateStr] = [];
                            }

                            availabilities[dateStr].push({
                                start_time: slot.start_time,
                                end_time: slot.end_time,
                                status: 'available',
                                event_type: slot.event_type
                            });
                        }
                    });
                }
                // If response is an array of slots
                else if (Array.isArray(response)) {
                    response.forEach((slot: any) => {
                        if (slot.start_time && slot.end_time) {
                            const date = new Date(slot.start_time);
                            const dateStr = formatDateToYYYYMMDD(date);
                            
                            if (!availabilities[dateStr]) {
                                availabilities[dateStr] = [];
                            }

                            availabilities[dateStr].push({
                                start_time: slot.start_time,
                                end_time: slot.end_time,
                                status: 'available',
                                event_type: slot.event_type
                            });
                        }
                    });
                }
            }

            setMonthAvailabilities(availabilities);
            lastFetchedMonthRef.current = monthKey;
        } catch (error) {
            console.error('Error fetching month availabilities:', error);
            setMonthAvailabilities({});
        } finally {
            setIsLoadingMonth(false);
        }
    }, [expert, auth.idToken]);

    // Fetch availabilities when component mounts or when currentMonth changes
    useEffect(() => {
        const monthKey = `${currentMonth.getFullYear()}-${currentMonth.getMonth()}`;
        if (lastFetchedMonthRef.current !== monthKey) {
            fetchMonthAvailabilities(currentMonth);
        }
    }, [currentMonth.getFullYear(), currentMonth.getMonth(), fetchMonthAvailabilities]);

    // Function to change month
    const changeMonth = (increment: number) => {
        const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + increment, 1);
        setCurrentMonth(newMonth);
        // Reset selected date and slot when changing months
        setSelectedDate(null);
        setSelectedSlot(null);
    };

    // Function to handle day selection
    const handleDayClick = (date: Date, hasAvailability: boolean) => {
        if (!hasAvailability) return;
        const dateStr = formatDateToYYYYMMDD(date);
        setSelectedDate(dateStr);
        setSelectedSlot(null);
    };

    // Function to handle slot selection
    const handleSlotSelect = (slot: TimeSlot) => {
        setSelectedSlot(slot);
    };

    // Function to handle booking
    const handleBook = () => {
        if (!selectedDate || !selectedSlot) return;
        
        // Show the Calendly widget with pre-filled data
        setShowCalendlyWidget(true);
    };

    // Function to handle Calendly widget close
    const handleCalendlyClose = () => {
        setShowCalendlyWidget(false);
        setSelectedDate(null);
        setSelectedSlot(null);
        onBookClick(expert);
    };

    // Function to format time slot
    const formatTimeSlot = (slot: TimeSlot) => {
        const start = new Date(slot.start_time);
        const end = new Date(slot.end_time);
        return `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')} - ${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`;
    };

    return {
        currentMonth,
        monthAvailabilities,
        isLoadingMonth,
        selectedDate,
        selectedSlot,
        showCalendlyWidget,
        getDaysInMonth,
        isToday,
        isPastDate,
        canNavigateToPreviousMonth,
        changeMonth,
        handleDayClick,
        handleSlotSelect,
        handleBook,
        handleCalendlyClose,
        formatTimeSlot,
        fetchMonthAvailabilities
    };
}; 